import React, { useEffect, useState } from "react";
import { withApollo } from "@apollo/client/react/hoc";
import { compose, path, pathOr } from "ramda";
import { withAuthProvider, getToken } from "../../../lib/auth";
import { LoadingComponent } from "@pga/pga-component-library";
import { buildAlgoliaQuery, extractQueryParams } from "@pga/algolia-utils";
import { useFeatureFlags, flags } from "../../../lib/feature-flags";
import { COACH_TOOLS_API } from "../../apolloClient";

import { initIndex } from "../../../lib/algolia";
import {
  JOB_CATEGORIES_QUERY,
  USER_JOB_PREFERENCES_QUERY,
} from "../../../queries/member";

export const RecommendedJobsContext = React.createContext();
export const useRecJobsContext = () => React.useContext(RecommendedJobsContext);

const jobsIndex = initIndex(
  process.env.REACT_APP_ALGOLIA_APP_ID,
  process.env.REACT_APP_ALGOLIA_API_KEY,
  process.env.REACT_APP_ALGOLIA_INDEX_NAME,
);

export const fetchJobsData = async ({
  setState,
  setLoading,
  client,
  isLoggedIn,
  authReady,
  me,
}) => {
  setLoading(true);

  // Always fetch job categories (doesn't require auth)
  let jobCategoriesData = { data: {} };
  try {
    const context = { api: COACH_TOOLS_API };
    jobCategoriesData = await client.query({
      query: JOB_CATEGORIES_QUERY,
      context,
    });
  } catch (error) {
    console.error("Failed to fetch job categories:", error);
    jobCategoriesData = { data: {} };
  }

  // Only fetch user preferences if authenticated
  let userPreferencesData = { data: {} };
  if (isLoggedIn && authReady && me) {
    try {
      // Double-check that we have a valid token before making the authenticated request
      const token = await getToken();
      if (!token) {
        console.warn(
          "No authentication token available, skipping user preferences query",
        );
        userPreferencesData = { data: {} };
      } else {
        userPreferencesData = await client.query({
          query: USER_JOB_PREFERENCES_QUERY,
          fetchPolicy: "no-cache", // Prevent caching issues with auth state
          errorPolicy: "all", // Continue even if there are GraphQL errors
          context: { api: COACH_TOOLS_API },
        });
      }
    } catch (error) {
      console.error("Failed to fetch job preferences:", error);
      // Continue with empty data to prevent the app from breaking
      userPreferencesData = { data: {} };
    }
  }

  const jobCategories = path(["jobCategories"], jobCategoriesData.data);
  const jobPreference = pathOr(
    {},
    ["me", "jobPreference"],
    userPreferencesData.data,
  );

  const algoliaQuery = buildAlgoliaQuery({
    queryParams: extractQueryParams(jobPreference, jobCategories),
    hitsPerPage: 150,
  });

  let recommendedJobs = [];
  if (algoliaQuery) {
    try {
      const { hits } = await jobsIndex.search(algoliaQuery);
      recommendedJobs = hits;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("Algolia search failed in RecommendedJobsProvider:", err);
      // Leave recommendedJobs as an empty array so the UI can continue to render.
    }
  }

  const { jobSearchStatus } = jobPreference;
  setState({
    data: jobSearchStatus
      ? {
          recommendedJobs,
          jobSearchStatus,
          recommendedJobsIds: (recommendedJobs || []).reduce(
            (ids, { objectID }) => ({ ...ids, [objectID]: true }),
            {},
          ),
        }
      : null,
  });

  setLoading(false);
};

export const RecommendedJobsProvider = ({
  children,
  client,
  isLoggedIn,
  authReady,
  me,
}) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchJobsData({
      setState,
      setLoading,
      client,
      isLoggedIn,
      authReady,
      me,
    });
  }, [authReady, isLoggedIn, me]);

  return (
    <RecommendedJobsContext.Provider value={{ ...state, loading }}>
      {children}
    </RecommendedJobsContext.Provider>
  );
};

export const AuthReadyWrapper = (props) =>
  props.authReady ? (
    <RecommendedJobsProvider {...props} />
  ) : (
    <LoadingComponent />
  );

export default compose(withApollo, withAuthProvider)(AuthReadyWrapper);
